import { useContext, useState } from 'react';
import { ScrapbookFile } from '@/api/scrapbook-data';
import ContentPreviewWraper from './ContentPreviewWraper';
import {
  AppInsightsContext,
  MetadataContext,
  TrackEventKey,
  TrackEventName,
  TrackEventType,
  UserContext,
} from '@/components';
import { fixColor, hexToRgb, isColorDark } from '@/utils';
import { useRouter } from 'next/router';
import classNames from 'classnames';

interface ColourPalettePreviewProps {
  content: ScrapbookFile | null;
  onClose: () => void;
}
const ColourPalettePreview = ({ content, onClose }: ColourPalettePreviewProps) => {
  const [selectedColor, setSelectedColor] = useState<any>(null);
  const user = useContext(UserContext);

  const appInsights = useContext(AppInsightsContext);
  const { pageId } = useContext(MetadataContext);
  const router = useRouter();
  if (!content) return null;

  const handleDownloadPalette = () => {
    appInsights.trackCustomEvent(TrackEventName.REPORT, TrackEventKey.COLOUR_PALETTE, TrackEventType.DOWNLOAD, user, {
      pageId,
      pagePath: router.asPath,
    });

    try {
      const form = document.getElementById('stDownloadPaletteForm') as HTMLFormElement;
      if (form) {
        const nameInput = form.querySelector('input[name="name"]') as HTMLInputElement;
        const colourIdsInput = form.querySelector('input[name="colourIds"]') as HTMLInputElement;

        if (nameInput && colourIdsInput) {
          nameInput.value = content.name || 'colours';
          colourIdsInput.value = content.content.colours.map((c: any) => c.id).join(',');
          form.submit();
        }
      }
    } catch (error) {
      console.error('Error downloading palette:', error);
    }
  };
  return (
    <ContentPreviewWraper
      content={content}
      onClose={onClose}
      title="Colour Palette"
      subTitle={content?.content?.name}
      onDownload={handleDownloadPalette}
    >
      <div className="flex">
        <div className="flex-1 p-6 pt-7">
          <div className="mb-6 flex flex-wrap">
            {content.content.colours.map((color: any) => (
              <div
                key={color.id}
                className="group relative min-w-5 flex-1 cursor-pointer"
                onClick={() => setSelectedColor(color)}
              >
                <div
                  className={classNames(
                    'h-50 bg-cover bg-center bg-no-repeat transition-all duration-200',
                    color?.effect === 'Metallic' && 'bg-metallic',
                    color?.effect === 'Lustre' && 'bg-lustre',
                    color?.effect === 'Chrome' && 'bg-chrome',
                  )}
                  style={{
                    backgroundColor: fixColor(color?.hexCode),
                  }}
                />
                {/* Tooltip */}
                <div
                  className="pointer-events-none absolute bottom-full left-1/2 z-20 mb-0.5 origin-bottom -translate-x-1/2 scale-0 transform rounded px-3 py-2 text-xs whitespace-nowrap text-white opacity-0 shadow-xl transition-all duration-300 ease-out group-hover:-translate-y-1 group-hover:scale-100 group-hover:opacity-100"
                  style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}
                >
                  {color.displayName || color.id}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div
          className={classNames(
            'my-7 mr-6 h-82 w-87 overflow-hidden rounded bg-cover bg-center bg-no-repeat p-6 shadow-lg',
            isColorDark(selectedColor?.hexCode) ? 'module-base-light' : 'module-base-dark',
            selectedColor?.effect === 'Metallic' && 'bg-metallic',
            selectedColor?.effect === 'Lustre' && 'bg-lustre',
            selectedColor?.effect === 'Chrome' && 'bg-chrome',
          )}
          style={{
            backgroundColor: fixColor(selectedColor?.hexCode),
          }}
        >
          {selectedColor ? (
            <div className="color-from-bg">
              <h3 className="mb-4 text-3xl font-normal">{selectedColor.displayName || selectedColor.id}</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <div className="mb-1 text-xs font-semibold">Pantone</div>
                  <div className="mb-4 text-base">{selectedColor.tpxCode || '--'}</div>
                  <div className="mb-1 text-xs font-semibold">Hex</div>
                  <div className="mb-4 text-base">#{selectedColor.hexCode || '--'}</div>
                  <div className="mb-1 text-xs font-semibold">RAL</div>
                  <div className="mb-4 text-base">{selectedColor.ralCode || '--'}</div>
                </div>
                <div>
                  <div className="mb-1 text-xs font-semibold">Coated</div>
                  <div className="mb-4 text-base">{selectedColor.coatedCode || '--'}</div>
                  <div className="mb-1 text-xs font-semibold">RGB</div>
                  <div className="mb-4 text-base">{hexToRgb(selectedColor.hexCode)?.join('/')}</div>
                  <div className="mb-1 text-xs font-semibold">NCS</div>
                  <div className="mb-4 text-base">{selectedColor.ncsCode || '--'}</div>
                </div>
              </div>
            </div>
          ) : (
            <div className="text-black/38">
              <p className="mb-4 font-semibold">Please select a colour to view the colour codes</p>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <div className="mb-1 text-base">Pantone</div>
                  <div className="mb-4 text-base">00-0000</div>
                  <div className="mb-1 text-xs font-semibold">Hex</div>
                  <div className="mb-4 text-base">#000000</div>
                  <div className="mb-1 text-xs font-semibold">RAL</div>
                  <div className="mb-4 text-base">000 00 00</div>
                </div>
                <div>
                  <div className="mb-1 text-xs font-semibold">Coated</div>
                  <div className="mb-4 text-base">000</div>
                  <div className="mb-1 text-xs font-semibold">RGB</div>
                  <div className="mb-4 text-base">000/000/000</div>
                  <div className="mb-1 text-xs font-semibold">NCS</div>
                  <div className="mb-4 text-base">0000-0000</div>
                </div>
              </div>
            </div>
          )}
        </div>
        {/* Hidden forms for downloads */}
        <form
          id="stDownloadPaletteForm"
          method="POST"
          target="_blank"
          action={`${process.env.NEXT_PUBLIC_API_URL || 'https://uat-web-api.testing-stylus.com'}/umbraco/Api/ColourPaletteRenderer/RenderPalette2`}
          style={{ display: 'none' }}
        >
          <input type="hidden" name="name" />
          <input type="hidden" name="colourIds" />
        </form>
      </div>
    </ContentPreviewWraper>
  );
};

export default ColourPalettePreview;
